import { useCallback } from 'react';

import { parseHtml } from '@change-corgi/core-react/html';
import { useI18n } from '@change-corgi/core-react/i18n';

import type { SessionUser } from '#src/shared/session';

import { useModalConfirmationContext } from '#src/app/shared/hooks/payments';

type Result = ModelHookResult<
	{
		display: boolean;
		showContinueAsGuest: boolean;
		googleAuthEnabled: boolean;
		user?: SessionUser;
		email: string;
		ariaLabel: string;
		title: string;
		separatorLabel: string;
		privacyNotice: React.ReactNode;
	},
	{
		handleSuccess: (user: SessionUser) => void;
		closeModal: () => void;
		continueAsGuest: () => void;
	}
>;

export function useLoginModal(): Result {
	const [{ modalOptions }, { handleModalResult }] = useModalConfirmationContext();
	const { translate } = useI18n();

	const options = modalOptions?.type === 'login' ? modalOptions : null;

	return {
		data: {
			display: options !== null,
			showContinueAsGuest: options?.showContinueAsGuest ?? false,
			googleAuthEnabled: options?.googleAuthEnabled ?? false,
			user: options?.user,
			email: options?.email ?? '',
			ariaLabel: translate('fe.components.login_or_join.log_in'),
			title: translate('fe.components.login_or_join.log_in'),
			separatorLabel: translate('corgi.login_or_signup.email.or'),
			privacyNotice: parseHtml(
				translate('fe.components.login_or_join.privacy_notice', {
					tosUrl: '/policies/terms-of-service',
					privacyUrl: '/policies/privacy',
				}),
				{
					allowedAttrs: ['target'],
				},
			),
		},
		actions: {
			handleSuccess: useCallback((user: SessionUser) => handleModalResult({ user }), [handleModalResult]),
			closeModal: useCallback(() => handleModalResult(undefined), [handleModalResult]),
			continueAsGuest: useCallback(
				() => handleModalResult({ user: undefined, continueAsGuest: true }),
				[handleModalResult],
			),
		},
	};
}
